import { PrankDefinition, Intensity } from './types';

const SLOW_BASENAME = 'snail.png';
const LEAD_BASENAME = 'chungus.png';

const INTENSE_COOLDOWN_MS = 3 * 60 * 60 * 1000;
const COOLDOWN_KEY_PREFIX = 'ui_anim_last_intense_';

const MAX_DIMENSION = 48;
const MIN_DIMENSION = 32;

const SLOW_LERP = 0.016;
const LEAD_FACTOR = 90;
const LEAD_LERP = 0.25;
const LEAD_RECOVER_LERP = 0.18;
const LEAD_STILL_THRESHOLD_MS = 120;
const VELOCITY_DECAY = 0.85;

const MANIFEST_ENDPOINT = '/internal-api/april-fools/cursor-images';

type IntenseMode = typeof SLOW_BASENAME | typeof LEAD_BASENAME;

interface CursorManifest {
  normal: Array<{ basename: string; url: string }>;
  intense: Record<string, string>;
}

interface AnchorOffset { x: number; y: number }

const ANCHORS: Record<IntenseMode | 'normal', (width: number, height: number) => AnchorOffset> = {
  [SLOW_BASENAME]: (width) => ({ x: width, y: 0 }),
  [LEAD_BASENAME]: (width) => ({ x: width / 2, y: 0 }),
  normal: () => ({ x: 0, y: 0 }),
};

let manifestPromise: Promise<CursorManifest | null> | null = null;

const fetchManifest = (): Promise<CursorManifest | null> => {
  if (manifestPromise) return manifestPromise;
  manifestPromise = fetch(MANIFEST_ENDPOINT, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then(response => (response.ok ? response.json() as Promise<CursorManifest> : null))
    .catch(() => null);
  return manifestPromise;
};

const pickRandom = <T>(items: T[]): T | null => (
  items.length === 0 ? null : items[Math.floor(Math.random() * items.length)]
);

const cooldownKey = (mode: IntenseMode): string => `${COOLDOWN_KEY_PREFIX}${mode.replace(/\.png$/, '')}`;

const isModeOnCooldown = (mode: IntenseMode): boolean => {
  const last = Number(window.localStorage.getItem(cooldownKey(mode)) ?? 0);
  return Number.isFinite(last) && Date.now() - last < INTENSE_COOLDOWN_MS;
};

const stampModeShown = (mode: IntenseMode): void => {
  window.localStorage.setItem(cooldownKey(mode), String(Date.now()));
};

const isTestMode = (): boolean => new URLSearchParams(window.location.search).has('knarp');

interface IntenseSelection {
  url: string;
  mode: IntenseMode;
}

const pickIntense = (manifest: CursorManifest): IntenseSelection | null => {
  const slowUrl = manifest.intense[SLOW_BASENAME.replace(/\.png$/, '')] ?? null;
  const leadUrl = manifest.intense[LEAD_BASENAME.replace(/\.png$/, '')] ?? null;
  const respectCooldown = !isTestMode();
  const candidates: IntenseSelection[] = [];
  if (slowUrl && (!respectCooldown || !isModeOnCooldown(SLOW_BASENAME))) {
    candidates.push({ url: slowUrl, mode: SLOW_BASENAME });
  }
  if (leadUrl && (!respectCooldown || !isModeOnCooldown(LEAD_BASENAME))) {
    candidates.push({ url: leadUrl, mode: LEAD_BASENAME });
  }
  return pickRandom(candidates);
};

const pickNormal = (manifest: CursorManifest): string | null => (
  pickRandom(manifest.normal.map(item => item.url))
);

const clampDimensions = (naturalWidth: number, naturalHeight: number): { width: number; height: number } => {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: MAX_DIMENSION, height: MAX_DIMENSION };
  }
  const scale = MAX_DIMENSION / Math.max(naturalWidth, naturalHeight);
  return {
    width: Math.max(MIN_DIMENSION, Math.round(naturalWidth * scale)),
    height: Math.max(MIN_DIMENSION, Math.round(naturalHeight * scale)),
  };
};

const attachCursor = ({ url, mode }: { url: string; mode: IntenseMode | 'normal' }): (() => void) => {
  const style = document.createElement('style');
  style.id = 'april-fools-cursor';
  style.textContent = `
    *, *::before, *::after {
      cursor: none !important;
    }
  `;
  document.head.appendChild(style);

  const img = document.createElement('img');
  img.id = 'april-fools-fake-cursor';
  img.src = url;
  img.alt = '';
  img.style.cssText = `
    position: fixed;
    pointer-events: none;
    z-index: 99999;
    user-select: none;
    max-width: ${MAX_DIMENSION}px;
    max-height: ${MAX_DIMENSION}px;
  `;
  document.body.appendChild(img);

  let anchor: AnchorOffset = ANCHORS[mode](MAX_DIMENSION, MAX_DIMENSION);
  img.addEventListener('load', () => {
    const { width, height } = clampDimensions(img.naturalWidth, img.naturalHeight);
    img.style.width = `${width}px`;
    img.style.height = `${height}px`;
    anchor = ANCHORS[mode](width, height);
  });

  let realX = 0;
  let realY = 0;
  let fakeX = 0;
  let fakeY = 0;
  let velocityX = 0;
  let velocityY = 0;
  let lastMoveAt = performance.now();

  const handleMouseMove = (e: MouseEvent) => {
    const now = performance.now();
    const dt = Math.max(1, now - lastMoveAt);
    velocityX = (e.clientX - realX) / dt;
    velocityY = (e.clientY - realY) / dt;
    realX = e.clientX;
    realY = e.clientY;
    lastMoveAt = now;
  };

  document.addEventListener('mousemove', handleMouseMove);

  let animFrame: number;
  const animate = () => {
    if (mode === SLOW_BASENAME) {
      fakeX += (realX - fakeX) * SLOW_LERP;
      fakeY += (realY - fakeY) * SLOW_LERP;
    } else if (mode === LEAD_BASENAME) {
      const sinceMove = performance.now() - lastMoveAt;
      const moving = sinceMove < LEAD_STILL_THRESHOLD_MS;
      if (moving) {
        const targetX = realX + velocityX * LEAD_FACTOR;
        const targetY = realY + velocityY * LEAD_FACTOR;
        fakeX += (targetX - fakeX) * LEAD_LERP;
        fakeY += (targetY - fakeY) * LEAD_LERP;
      } else {
        velocityX *= VELOCITY_DECAY;
        velocityY *= VELOCITY_DECAY;
        fakeX += (realX - fakeX) * LEAD_RECOVER_LERP;
        fakeY += (realY - fakeY) * LEAD_RECOVER_LERP;
      }
    } else {
      fakeX = realX;
      fakeY = realY;
    }
    img.style.left = `${fakeX - anchor.x}px`;
    img.style.top = `${fakeY - anchor.y}px`;
    animFrame = window.requestAnimationFrame(animate);
  };
  animFrame = window.requestAnimationFrame(animate);

  return () => {
    window.cancelAnimationFrame(animFrame);
    document.removeEventListener('mousemove', handleMouseMove);
    style.remove();
    img.remove();
  };
};

const lazyCursorPrank: PrankDefinition = {
  namedCode: 'cursor',
  hexCode: 'c7e4',
  create: (intensity) => {
    const isIntense = intensity === Intensity.INTENSE;
    let cleanup: (() => void) | null = null;
    let cancelled = false;

    fetchManifest().then((manifest) => {
      if (cancelled || !manifest) return;
      const intenseSelection = isIntense ? pickIntense(manifest) : null;
      const url = intenseSelection?.url ?? pickNormal(manifest);
      if (!url) return;
      const mode: IntenseMode | 'normal' = intenseSelection?.mode ?? 'normal';
      if (intenseSelection && !isTestMode()) {
        stampModeShown(intenseSelection.mode);
      }
      cleanup = attachCursor({ url, mode });
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  },
};

export default lazyCursorPrank;
